/**
 * The **rooms** half of the theme contract (`docs/guest-pages-design.md`,
 * decisions 4–7): the nav bar's shape, the rule that turns a category's name
 * into a URL, and the caps on the blurb a tenant may write over a category page.
 *
 * It sits beside `./sections.ts` for the same reason that file sits beside
 * `./tokens.ts`: two very different consumers — a tolerant browser bundle and a
 * strict operator surface — must agree on this vocabulary *letter for letter*,
 * and the only way that stays true is for the letters to exist once.
 *
 * ## Why the nav is here and not a section
 *
 * A block is a thing arranged **on** the home page. The nav is the frame around
 * every page including the ones the home page does not contain, so it cannot be
 * an entry in `sections` without the category page having to render a section it
 * is not part of. It is therefore a branding key of its own (`nav`), with the
 * same "adding is free" rule as everything else: a tenant with no `nav` gets the
 * minimal shell the bundle shipped before this existed, byte for byte.
 */
import { isContentPageSlug } from './content.js';
/* ------------------------------------------------------------------ */
/* Category slugs — the URL rule                                       */
/* ------------------------------------------------------------------ */
/**
 * The longest slug a category name may become. Sixty characters is four or five
 * English words, which is longer than any heading anybody puts over a grid of
 * photographs, and short enough that a URL stays a thing a person can read out.
 */
export const CATEGORY_SLUG_MAX = 60;
/** What a slug may be, once derived. Lower-case, digits, single hyphens. */
export const CATEGORY_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * **The slug rule, stated once and implemented once.**
 *
 * `"Jeep Adventures"` → `jeep-adventures`. `"Tratamientos insignia"` →
 * `tratamientos-insignia`. `"Café & Co."` → `cafe-co`.
 *
 * The steps, in order:
 *
 *  1. Unicode NFKD, then drop combining marks — so `é` becomes `e` rather than
 *     being deleted. A Spanish category must produce a Spanish-looking slug, not
 *     a gap where the accents were.
 *  2. Lower-case.
 *  3. Every run of characters that is not `[a-z0-9]` becomes a single `-`.
 *  4. Trim leading and trailing hyphens, then cut to `CATEGORY_SLUG_MAX` and
 *     trim again (so a cut never leaves a dangling hyphen).
 *
 * The result is `''` for a name with no Latin letters or digits in it at all —
 * a category named only in a script this rule cannot transliterate. `''` is not
 * a slug: the caller drops that category from the routing table rather than
 * inventing an identifier, and the items still appear everywhere else they
 * appear. Guessing (`category-1`) would produce a URL that changes the moment
 * another such category is added, which is the one thing a permalink may not do.
 *
 * ## Which language the slug is derived from, and why it is not the reader's
 *
 * **English, always** — `localize(category, 'en', …)`, resolved by the caller
 * before it gets here. A slug derived from the language of the request would
 * give one category two permanent addresses, split every inbound link between
 * them, and make a shared URL open a page that does not exist for the person it
 * was shared with. The page's *heading* is localized; its address is not, in the
 * same way `svc_massage_60` is the id in every language (i18n-design §4).
 *
 * ## Collisions
 *
 * Two different category names can derive the same slug (`"Sunset Runs"` and
 * `"Sunset — runs"`). Nothing here prevents that and nothing downstream
 * disambiguates it: **a slug names a set of items, and a collision merges the
 * two sets onto one page whose heading is the first category name in catalog
 * order.** That is the honest failure — every item is still reachable, still
 * linked from the grid, and still has its own item page — and it is visible to
 * the owner the moment they look at the page. The alternative, minting
 * `sunset-runs-2` for whichever category was written second, would make a
 * public URL depend on insertion order, which is worse in every direction.
 */
/** The Unicode combining marks NFKD splits an accented letter into. */
const COMBINING_MARKS = /[̀-ͯ]/g;
export function categorySlug(name) {
    const folded = name
        .normalize('NFKD')
        .replace(COMBINING_MARKS, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return folded.slice(0, CATEGORY_SLUG_MAX).replace(/-+$/g, '');
}
/* ------------------------------------------------------------------ */
/* The category blurb zone                                             */
/* ------------------------------------------------------------------ */
/**
 * The words over a category page's grid — a content zone (rung 4), sanitized on
 * publish exactly like `story.body_html` and the footer's fine print.
 *
 * Smaller than `RICHTEXT_MAX` on purpose: a category page's job is the grid, and
 * four thousand characters is already three or four paragraphs of introduction
 * above the first photograph. The ceiling that actually binds is still
 * `MAX_DOCUMENT_BYTES` on the whole branding row, which is why the number of
 * zones is capped too.
 */
export const CATEGORY_BLURB_MAX = 4_000;
/**
 * How many categories may carry a blurb.
 *
 * Twenty-four matches the section arrangement's ceiling for the same reason: a
 * business with more than two dozen categories on its booking site has a
 * catalog problem rather than a branding one, and every one of these rides in
 * the row `guest-api` reads in full on every page load.
 */
export const CATEGORY_ZONES_MAX = 24;
/* ------------------------------------------------------------------ */
/* The nav                                                             */
/* ------------------------------------------------------------------ */
/**
 * Seven links, and the number is a design decision rather than a round one.
 *
 * A nav bar is read on a phone in one line. Five to seven items is where a real
 * small-business site lands (Pink Perry VI runs five: Story, Adventures,
 * Occasions, FAQ, Shop) and past that the bar wraps, which is the point at which
 * a *page* would have been the better answer than a link.
 */
export const NAV_MAX_ENTRIES = 7;
/** A label is one or two words. Forty characters is generous for two. */
export const NAV_LABEL_MAX = 40;
/**
 * **The nav is internal-only, and the target grammar is what makes that
 * structural rather than a rule somebody enforces.**
 *
 * A target is one of exactly three shapes:
 *
 *  - `section:<id>` — an anchor on the home page (`SECTION_ID_PATTERN`);
 *  - `category:<slug>` — that category's page, `#/c/<slug>`;
 *  - `page:<slug>` — a content page, `#/p/<slug>` (`./content.ts`);
 *  - `visits` — the built-in "your visits" view, `#/visits`.
 *
 * There is deliberately no shape that can hold a URL. `https://example.com` is
 * not a malformed target that gets refused by a check; it does not parse as a
 * target at all, which means no future edit to a validator can accidentally let
 * one through. The *why* an operator is told: a guest's booking page carries the
 * tenant's brand and this platform's session cookie, and a link the platform
 * cannot vouch for on that page is a phishing surface with the tenant's name on
 * it. A business's Instagram belongs in the footer rows, where it reads as a
 * fact about the business rather than as part of the site's own navigation.
 */
export const NAV_BUILTIN_TARGETS = ['visits', 'cart', 'track'];
/** The prefixes, named so neither consumer writes the string twice. */
export const NAV_SECTION_PREFIX = 'section:';
export const NAV_CATEGORY_PREFIX = 'category:';
/**
 * `page:<slug>` — a content page of the tenant's own (`./content.ts`,
 * `docs/site-section-system.md` §9.6 decision 5). The shape the FAQ, the
 * story and the occasions page move onto now that they have addresses: a bar
 * entry that used to scroll to a band on the home page points at a page
 * instead, and the band leaves the home.
 *
 * Structurally it is the `category:` shape with a different prefix and a
 * different slug rule, which is the point — internal-only stays internal-only
 * because there is still no shape a URL can occupy.
 */
export const NAV_PAGE_PREFIX = 'page:';
/**
 * `item:<id>` — one catalog item's own page. Joined the vocabulary when the
 * showcase's slices learned to link to individual lodges. Ids are seed-minted
 * and NOT stable across a reseed, so hand-authoring one into a config row is a
 * footgun — the AlpineCreek seeder injects the ids it just created, which is
 * the pattern any generated document should follow.
 */
export const NAV_ITEM_PREFIX = 'item:';
/**
 * What a catalog item id may look like **to a branding document**.
 *
 * Deliberately a shape rather than a prefix rule: ids are prefixed ULIDs today
 * (`sv_…`, `it_…`) and this contract is not the place that decides which
 * prefix a sellable thing wears. The source string is exported because a
 * second consumer now needs the same rule — `booking.services` is a list of
 * these, and its `SlotSpec.entry_pattern` is compiled from this exact text so
 * that a link to an item and a band scoped to one cannot disagree about what
 * an item id is.
 */
export const ITEM_ID_PATTERN = '^[A-Za-z0-9_-]{1,64}$';
export const ITEM_ID_MAX = 64;
const ITEM_ID = new RegExp(ITEM_ID_PATTERN);
const SECTION_ID = /^[a-z0-9-]{1,40}$/i;
/**
 * Parse a target string, or `null` for anything that is not one.
 *
 * `null` is what both consumers act on and they act on it differently, which is
 * the same asymmetry the rest of the contract runs on: the bundle skips the
 * entry with a console warning (one static build serves every hostname, so a row
 * written against next week's vocabulary is ordinary traffic), and `admin-api`
 * answers `400` with the sentence above.
 */
export function parseNavTarget(target) {
    if (NAV_BUILTIN_TARGETS.includes(target)) {
        return { kind: 'builtin', name: target };
    }
    if (target.startsWith(NAV_SECTION_PREFIX)) {
        const id = target.slice(NAV_SECTION_PREFIX.length);
        return SECTION_ID.test(id) ? { kind: 'section', id } : null;
    }
    if (target.startsWith(NAV_CATEGORY_PREFIX)) {
        const slug = target.slice(NAV_CATEGORY_PREFIX.length);
        return slug.length <= CATEGORY_SLUG_MAX && CATEGORY_SLUG_PATTERN.test(slug)
            ? { kind: 'category', slug }
            : null;
    }
    if (target.startsWith(NAV_PAGE_PREFIX)) {
        const slug = target.slice(NAV_PAGE_PREFIX.length);
        return isContentPageSlug(slug) ? { kind: 'page', slug } : null;
    }
    if (target.startsWith(NAV_ITEM_PREFIX)) {
        const id = target.slice(NAV_ITEM_PREFIX.length);
        return ITEM_ID.test(id) ? { kind: 'item', id } : null;
    }
    return null;
}
/**
 * Where a parsed target points, as an href this bundle's router understands.
 *
 * One function so the nav bar, the SSR renderer and any future breadcrumb agree
 * about what `category:jeep-adventures` means. Hash routes, for `App.tsx`'s
 * stated reason: a hash never reaches the server, so a deep link works with no
 * bucket rewrite and no per-tenant configuration — and, since the pages wave,
 * `guest-api` serves the *same* content at a real path for crawlers, with the
 * bundle taking over on hydration.
 */
export function navHref(target) {
    switch (target.kind) {
        case 'section':
            return `#${target.id}`;
        case 'category':
            return `#/c/${target.slug}`;
        case 'page':
            return `#/p/${target.slug}`;
        case 'item':
            return `#/i/${target.id}`;
        case 'builtin':
            return `#${BUILTIN_PATHS[target.name]}`;
    }
}
/**
 * Where each built-in lives, as a **path** — so the nav bar, the router and the
 * SSR route table read one table rather than three switch statements.
 *
 * `cart` and `track` joined `visits` with the shop (`docs/fulfillment-design.md`
 * wave 2). All three are the same kind of thing: an affordance the platform
 * provides, which a tenant may **name and place** but may not remove — the rule
 * decision 6 states for `visits` and which does not get weaker because the room
 * behind it holds a parcel instead of a booking.
 */
export const BUILTIN_PATHS = {
    visits: '/visits',
    cart: '/cart',
    track: '/track',
};
/* ------------------------------------------------------------------ */
/* The page routes                                                     */
/* ------------------------------------------------------------------ */
/**
 * The four page shapes, as *paths* — the form `guest-api` serves to a crawler
 * and the form the load balancer routes (decision 11).
 *
 * They are declared here rather than in either consumer because three surfaces
 * need them to agree: the bundle's router (which reads them out of the hash),
 * the server renderer (which reads them out of the path), and the sitemap
 * (which writes them). A fourth spelling in a Terraform file is unavoidable —
 * a URL map cannot import TypeScript — and `docs/dev.md` carries the note.
 *
 * `page` is the fourth shape (`docs/site-section-system.md` §9.6 decision 5):
 * a content page is `/p/<slug>`, hash `#/p/<slug>`, and the LB's URL map and
 * `guest-api`'s renderer learn it exactly the way they learned `/c/` — one
 * more prefix in the same table, one more branch in the same route function,
 * one more block of entries in the same sitemap. Nothing about it is a new
 * kind of thing; that is what made it worth having a table.
 */
export const PAGE_PATHS = {
    home: '/',
    category: '/c/',
    item: '/i/',
    page: '/p/',
};
//# sourceMappingURL=pages.js.map